import { useInternational } from '@/common/locale';
import { computed, ref, watch } from 'vue';
import { NotificationType } from '@/alert/utils/notification.types';
import {
  ALERT_TYPE_VALUE,
  CHECK_BY_TARGET,
  EVENT_NAME_ENUM,
  EVENT_TYPE_ENUM,
} from '@/alert/utils/define';
import {
  getV9AlertsAlertRulesByAlertRuleId,
  getV9AlertsAlertRulesSystemByAlertRuleId,
} from '@/api-client/alert/queries/alert-rule-v9-controller/alert-rule-v9-controller';
import { getV9AlertEventDetail } from '@/api-client/alert/queries/event-alert-controller/event-alert-controller';
import type { SystemAlertDetail } from '@/api-client/alert/model/system-alert-detail';
import { useReasonData } from '@/config/components/reasonSelect/reasonSelect.uses';
import { useStatInfoStore } from '@/common/stores/stat-info';
import { useAlertValue } from '@/alert/components/alertGrid/alertGrid.setup';
import type {
  EventAlertMessageItem,
  ResponseEventConditionV9,
  ResponseMetricConditionV9,
  ResponseSystemCondition,
  AlertRuleDetailV9,
} from '@/api-client/alert/model';
import type {
  TargetInfo,
  OccurrenceItem,
  RuleGroup,
  ProcessedMetricInfo,
  RuleData,
} from './eventAlertInformation.types';
import { PATH_COLUMNS } from './eventAlertInformation.define';
import { useMyInfoStore } from '@/config/stores/my-info';
import {
  convertRuleApiToView,
  convertMetricInfo,
  findRuleMetricThreshold,
  buildRuleGroups,
  buildOccurrenceItems,
  convertApiToRuleData,
  convertLoggingConditionToPathRows,
} from './eventAlertInformation.utils';
import { resolveAlertMetricSubType } from '@/alert/utils/alertMetricSubType.utils';

export interface Props {
  targetInfo: TargetInfo;
  isShow: boolean;
  eventData?: EventAlertMessageItem;
  ruleData?: AlertRuleDetailV9;
  isFetching?: boolean;
}

export interface Emit {
  (e: 'onClickStatusIcon', iconType: NotificationType, resultId: string): void;
  (e: 'change-loading', value: boolean): void;
}

export const setup = (props: Props, emits: Emit) => {
  const { t } = useInternational();
  const { language } = useMyInfoStore().myInfo;
  const { fetchReason, getReasons } = useReasonData();
  const statInfoStore = useStatInfoStore();
  // @ts-expect-error TODO: FIXME
  const { getAlertValue } = useAlertValue();

  // 시스템 알람 상세 데이터 (그리드용)
  const systemAlertDetails = ref<SystemAlertDetail[]>([]);

  const ruleData = ref<RuleData>({
    alertRuleName: '',
    alertRuleType: '',
    ruleCriteria: '',
    checkInterval: 0,
    checkRepeat: 0,
    maximumOccurrences: 0,
    checkHoldTime: 'p0s',
    weeklySchedule: {
      timeZone: '',
      dayWeek: '',
      startTime: '',
      endTime: '',
    },
  });

  // 이벤트 알람 상세 데이터
  const eventAlertMessageItem = ref<EventAlertMessageItem | undefined>(undefined);
  const eventConditions = ref<ResponseEventConditionV9[]>([]);
  const metricConditions = ref<ResponseMetricConditionV9[]>([]);
  const ruleMetricConditions = ref<ResponseMetricConditionV9[]>([]);
  const systemCondition = ref<ResponseSystemCondition | undefined>(undefined);
  const occurrenceItems = ref<OccurrenceItem[]>([]);

  const onClickStatusIcon = async (iconType: NotificationType) => {
    const resultId = props.targetInfo.resultId ?? '';
    emits('onClickStatusIcon', iconType, resultId);
  };

  const getStatName = (category: string, statId: string): string => {
    const selectedStatInfo = statInfoStore.getStatInfo({
      childCategory: category,
      statId,
    });
    return selectedStatInfo?.name ?? statId;
  };

  const updateOccurrenceItems = () => {
    occurrenceItems.value = buildOccurrenceItems(
      eventAlertMessageItem.value,
      eventConditions.value,
      metricConditions.value,
      systemCondition.value,
      t,
      ruleData.value.alertRuleType,
      ruleData.value.ruleCriteria,
      getReasons,
      getStatName,
      props.eventData?.notificationStatus,
      getAlertValue,
    );
  };

  // 데이터 조회 (룰 데이터 + 이벤트 알람 상세 데이터)
  const fetchData = async () => {
    const { ruleId: alertRuleId, resultId, info } = props.targetInfo;
    if (!alertRuleId || !resultId) return;

    const isSystemType = info?.[0]?.type?.toLowerCase() === 'system';

    try {
      emits('change-loading', true);

      if (isSystemType) {
        // System 타입: EventAlertDetail API만 호출
        let tempEventData = props.eventData;

        if (!tempEventData) {
          const eventResponse = await getV9AlertEventDetail({
            alertRuleId,
            resultId,
          });
          tempEventData = eventResponse.data?.data?.[0];
        }

        if (tempEventData) {
          const systemCond = tempEventData.messageInfo?.systemConditions;

          // System 타입 룰 데이터 설정
          ruleData.value = {
            alertRuleName: tempEventData.alertRuleName ?? '',
            alertRuleType: 'System',
            ruleCriteria: CHECK_BY_TARGET,
            checkInterval: 0,
            checkRepeat: 0,
            maximumOccurrences: 0,
            checkHoldTime: 'p0s',
            weeklySchedule: {
              timeZone: '',
              dayWeek: '',
              startTime: '',
              endTime: '',
            },
            platform: systemCond?.platform,
          };

          eventAlertMessageItem.value = tempEventData;
          systemCondition.value = systemCond;
          eventConditions.value = [];
          metricConditions.value = [];
          ruleMetricConditions.value = [];
        }

        // 시스템 알람 룰 상세 데이터 조회 (그리드용)
        try {
          const systemResponse = await getV9AlertsAlertRulesSystemByAlertRuleId(alertRuleId);
          systemAlertDetails.value = systemResponse?.data?.data ?? [];
        } catch (e) {
          console.log(e);
          systemAlertDetails.value = [];
        }
      } else {
        // System 타입이 아닐 때: 두 API 모두 호출
        let tempRuleData = props.ruleData;
        let tempEventData = props.eventData;

        if (!tempRuleData || !tempEventData) {
          const [ruleResponse, eventResponse] = await Promise.all([
            getV9AlertsAlertRulesByAlertRuleId(alertRuleId),
            getV9AlertEventDetail({ alertRuleId, resultId }),
          ]);

          tempRuleData = ruleResponse.data?.data?.[0];
          tempEventData = eventResponse.data?.data?.[0];
        }

        // 룰 데이터 설정
        if (tempRuleData) {
          ruleData.value = convertApiToRuleData({
            ...tempRuleData,
            alertMode: tempEventData?.alertMode,
          });
          ruleMetricConditions.value = tempRuleData.metricConditions ?? [];
        }

        // 이벤트 알람 상세 데이터 설정
        if (tempEventData) {
          const conditions = tempEventData.messageInfo?.eventConditions ?? [];

          if (conditions.length > 0 && conditions[0].eventType === EVENT_TYPE_ENUM.KUBERNETES) {
            const kind = conditions[0].variableConditionList?.[0]?.kind?.[0];
            if (kind) {
              await fetchReason(kind);
            }
          }

          eventAlertMessageItem.value = tempEventData;

          // 프로세스 수 변동 감지: fired 응답에 대상/임계치가 누락되어 룰 조건 값으로 보완
          const ruleEventCondition = tempRuleData?.eventConditions?.[0];
          if (
            conditions[0]?.eventType === EVENT_NAME_ENUM.PROCESS_COUNT_DETECTION &&
            ruleEventCondition
          ) {
            const ruleThreshold = ruleEventCondition as {
              comparison?: string;
              warningThreshold?: number | null;
              criticalThreshold?: number | null;
            };
            eventConditions.value = [
              {
                ...conditions[0],
                targets: conditions[0].targets?.length
                  ? conditions[0].targets
                  : ruleEventCondition.targets,
                comparison: ruleThreshold.comparison,
                warningThreshold: ruleThreshold.warningThreshold,
                criticalThreshold: ruleThreshold.criticalThreshold,
              } as ResponseEventConditionV9,
              ...conditions.slice(1),
            ];
          } else {
            eventConditions.value = conditions;
          }

          metricConditions.value = tempEventData.messageInfo?.metricConditions ?? [];
          systemCondition.value = undefined;
        }
      }

      updateOccurrenceItems();
    } catch (e) {
      console.log(e);
    } finally {
      emits('change-loading', false);
    }
  };

  // ruleId 또는 isShow 변경 시 데이터 조회
  watch(
    () =>
      [props.targetInfo.ruleId, props.targetInfo.resultId, props.isShow, props.isFetching] as const,
    async ([newRuleId, newResultId, newIsShow, newIsFetching]) => {
      if (newIsShow && newRuleId && newResultId && !newIsFetching) {
        await fetchData();
      }
    },
    { immediate: true },
  );

  const ruleViewData = computed(() => convertRuleApiToView(ruleData.value, t));

  const processedMetricInfos = computed<ProcessedMetricInfo[]>(() => {
    const isTableType = ruleData.value.alertRuleType === ALERT_TYPE_VALUE.TABLE;

    return metricConditions.value.map((metric, index) => {
      const metricInfo = convertMetricInfo(
        metric,
        findRuleMetricThreshold(metric, ruleMetricConditions.value, index),
      );
      const selectedStatInfo = statInfoStore.getStatInfo({
        childCategory: metricInfo.category,
        statId: metricInfo.alertStatName,
      });

      if (isTableType) {
        const tableUnit = (metric.threshold as { unit?: string } | undefined)?.unit?.trim() ?? '';
        return {
          ...metricInfo,
          alertStatName: selectedStatInfo?.name ?? metricInfo.alertStatName,
          unit: tableUnit,
        };
      }

      return {
        ...metricInfo,
        alertStatName: selectedStatInfo?.name ?? metricInfo.alertStatName,
        unit: selectedStatInfo?.unit ?? '',
      };
    });
  });

  const pathRows = computed(() =>
    convertLoggingConditionToPathRows(ruleData.value.loggingCondition),
  );

  const ruleGroups = computed<RuleGroup[]>(() =>
    buildRuleGroups(ruleViewData.value, t, metricConditions.value),
  );

  const metricConditionSubType = computed(() => {
    if (ruleData.value.alertRuleType !== ALERT_TYPE_VALUE.METRIC) {
      return undefined;
    }
    return resolveAlertMetricSubType({
      subType: ruleData.value.subType,
      ruleCriteria: ruleData.value.ruleCriteria,
      metricConditions: metricConditions.value,
    });
  });

  const contentItemMinWidth = computed(() => (language === 'KO' ? '116px' : '170px'));

  // 프로세스 상태 감지 / 프로세스 수 변동 감지: 정보 탭에 대상 호스트의 '프로세스 목록' 섹션 노출
  const isProcessStatusDetection = computed(
    () =>
      ruleViewData.value.event === EVENT_NAME_ENUM.PROCESS_DOWN_DETECTION ||
      ruleViewData.value.event === EVENT_NAME_ENUM.PROCESS_COUNT_DETECTION,
  );
  // 대상 호스트 ID (targetId가 'hostId|pid' 형태일 수 있어 hostId만 추출)
  const processHostId = computed(
    () => (eventConditions.value?.[0]?.targets?.[0]?.targetId ?? '').split('|')[0],
  );

  return {
    t,
    PATH_COLUMNS,
    onClickStatusIcon,
    ruleData: ruleViewData,
    metricInfos: processedMetricInfos,
    pathRows,
    occurrenceItems,
    ruleGroups,
    contentItemMinWidth,
    alertRuleType: computed(() => ruleData.value.alertRuleType),
    ruleCriteria: computed(() => ruleData.value.ruleCriteria),
    metricConditionSubType,
    systemAlertDetails,
    isProcessStatusDetection,
    processHostId,
  };
};
